import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { styled } from '@mui/material/styles';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import {
  GridCallbackDetails,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useDebouncedRefetch } from '@/hooks/useDebouncedRefetch';

import { DEFAULT_PAGE } from '@/data/constants';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';

import { SortModel } from '@/data/models/sort-model.model';
import { TripLegExceptionBC } from '@/data/models/trip-leg-exception.model';
import { FilterModel } from '@/data/models/filters.model';
import { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';

import { TripLegEventType } from '@/data/events/trip-leg';

import * as lookupSlice from '@/data/lookup.slice';
import {
  fetchMonitoringExceptionsAsync,
  fetchTripsByReferenceIdsAsync,
  removeMonitoringExceptionsItem,
  removeTripLegEventFromQueue,
  selectAllTripLegs,
  selectLoadingMonitoringExceptions,
  selectMonitoringExceptions,
  selectTripLegEventQueue,
} from '@/data/slices/trip-leg.slice';

import { MonitoringExceptionsTable } from './MonitoringExceptionsTable';
import { TripLegSearchMenu } from '@/data/requests/trip-legs';
import realtimeUtils from '@/services/webSocketService/realtimeUtils';

const PREFIX = 'MonitoringExceptions';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  filter: `${PREFIX}-filter`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(3), // Adds bottom margin to all child components except the last one
    },
    boxShadow: '0px 0px 10px #dfdfdf',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
    flexGrow: 1,
  },
  [`& .${classes.filter}`]: {
    width: 200,
  },
}));

const EXCEPTION_REFRESH_EVENT_TYPES = [
  TripLegEventType.TripLegException,
  TripLegEventType.TripLegExceptionAssigned,
  TripLegEventType.TripLegExceptionCleared,
];

export default function MonitoringExceptions() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const FILTER_ALL = [
    TripLegExceptionBC.DriverLate,
    TripLegExceptionBC.OpenSameDay,
    TripLegExceptionBC.NoDriver,
    TripLegExceptionBC.DriverNotOnline,
    TripLegExceptionBC.EtaNotUpdating,
  ];
  const [currentFilter, setCurrentFilter] = useState<string[]>(FILTER_ALL);
  const [page, setPage] = useState(DEFAULT_PAGE);
  // Deviate from standard page size for this page specifically F1V2-3749
  const [pageSize, setPageSize] = useState(100);
  const [sortModel, setSortModel] = useState<SortModel>({
    column: 'appointmentDate,pickUpDueTimeUTC',
    direction: 'asc,asc',
  });

  const tripLegEventQueue = useAppSelector(selectTripLegEventQueue);
  const allTripLegs = useAppSelector(selectAllTripLegs);
  const monitoringExceptions = useAppSelector(selectMonitoringExceptions);
  const loadingMonitoringExceptions = useAppSelector(
    selectLoadingMonitoringExceptions,
  );

  const sortedTripLegs = useMemo(() => {
    return [...monitoringExceptions.items].sort(
      (a, b) => Number(Boolean(a.isWillCall)) - Number(Boolean(b.isWillCall)),
    );
  }, [monitoringExceptions.items]);

  const filterOptions = [
    { label: t('triplegInventory.filterAll'), value: FILTER_ALL },
    {
      label: t('tripLegException.open'),
      value: [TripLegExceptionBC.OpenSameDay],
    },
    {
      label: t('tripLegException.driverLate'),
      value: [TripLegExceptionBC.DriverLate],
    },
    {
      label: t('tripLegException.noDriver'),
      value: [TripLegExceptionBC.NoDriver],
    },
    {
      label: t('tripLegException.driverNotOnline'),
      value: [TripLegExceptionBC.DriverNotOnline],
    },
    {
      label: t('tripLegException.etaNotUpdating'),
      value: [TripLegExceptionBC.EtaNotUpdating],
    },
  ];

  const handlePaginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== monitoringExceptions.currentPage - 1) {
      setPage(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      setPageSize(model.pageSize);
    }
  };

  const handleTripLegFilterChange = (event: any) => {
    const f = event.target.value;
    setCurrentFilter(f?.split(','));
  };

  const handleSortModelChange = (
    model: GridSortModel,
    details: GridCallbackDetails<any>,
  ) => {
    let newModel: SortModel | null = null;
    if (model?.length) {
      if (model[0].field === 'pickUpDueTimeUTC') {
        newModel = {
          column: 'appointmentDate,pickUpDueTimeUTC',
          direction: model[0].sort === 'desc' ? 'desc,desc' : 'asc,asc',
        };
      } else {
        newModel = {
          column: model[0].field,
          direction: model[0].sort,
        };
      }
    }
    if (newModel) {
      setPage(1);
      setSortModel(newModel);
    } else {
      setPage(1);
      setSortModel({
        column: 'appointmentDate,pickUpDueTimeUTC',
        direction: 'asc,asc',
      });
    }
  };

  const activeSearchRequestRef = useRef<AbortController | null>(null);
  const hasFetchedInitialDataRef = useRef(false);
  const fetchTripsWithExceptions = useCallback(() => {
    activeSearchRequestRef.current?.abort();
    const abortController = new AbortController();
    activeSearchRequestRef.current = abortController;

    const today = DateTime.now().toISODate();
    const syncIdsOnly = hasFetchedInitialDataRef.current;
    hasFetchedInitialDataRef.current = true;

    dispatch(
      fetchMonitoringExceptionsAsync({
        page,
        pageSize,
        sort: sortModel?.column ?? undefined,
        sortOrder: sortModel?.direction ?? undefined,
        menu: TripLegSearchMenu.MONITORING_EXCEPTIONS,
        pickupDateFrom: today,
        pickupDateTo: today,
        tripLegExceptionBCs: currentFilter,
        todayDate: DateTime.local().toFormat('yyyy-MM-dd'),
        syncIdsOnly,
        signal: abortController.signal,
      }),
    );
  }, [dispatch, page, pageSize, sortModel, currentFilter]);

  const requestRefetch = useDebouncedRefetch(fetchTripsWithExceptions, 200);

  // Aborted only on unmount, for requests that must not cancel one another
  // (e.g. Will Call hydrations for different trip legs). Created in the effect
  // rather than at render so a StrictMode remount gets a fresh, unaborted one.
  const unmountAbortControllerRef = useRef<AbortController | null>(null);

  // Abort any in-flight request on unmount.
  useEffect(() => {
    const unmountAbortController = new AbortController();
    unmountAbortControllerRef.current = unmountAbortController;
    return () => {
      activeSearchRequestRef.current?.abort();
      unmountAbortController.abort();
    };
  }, []);

  // General refetch of page for pagination, sort, and filter changes.
  useEffect(() => {
    requestRefetch();
  }, [fetchTripsWithExceptions, page, pageSize, sortModel, currentFilter]);

  // Real time updates for exceptions, exception assignments, exception
  // clears, and Will Call activations. Only a relevant event (one that
  // would change what's visible on the page currently being viewed)
  // triggers a refetch, so an event for a trip leg on a different page
  // doesn't disturb the user's current view.
  useEffect(() => {
    if (tripLegEventQueue.length === 0) {
      return;
    }

    // removeTripLegEventFromQueue (below) trims every queued event up to and
    // including the last one we inspect, so every event in the current
    // batch has to be evaluated here in one pass -- looking at only the
    // last event would silently discard earlier ones in the same batch
    // without ever considering whether they were relevant (e.g. an earlier
    // clear for a different trip leg, or an earlier Will Call activation
    // that should now sort onto this page but is followed by an unrelated
    // off-page event).
    let removedVisibleClear = false;
    let needsGeneralRefetch = false;
    const visibleActivatedTripLegReferenceIDs = new Set<string>();

    // Shared by both shouldRefreshTripLegData calls below. itemCount lets
    // doesTripLegSortToCurrentPage derive the real last page instead of
    // guessing from the current page's own row count (which is wrong for
    // any page but the actual last one).
    const realtimeFilters = {
      ...monitoringExceptions.filters,
      sort: sortModel.column,
      sortOrder: sortModel.direction,
      tripLegExceptionBC: currentFilter,
      itemCount: monitoringExceptions.itemCount,
    } as FilterModel;

    tripLegEventQueue.forEach((event, index) => {
      const eventType = event.detailType as TripLegEventType;
      const tripLegReferenceID = event.detail?.tripLegReferenceID as string;
      const isOnCurrentPage = monitoringExceptions.items.some(
        (item) => item.tripLegReferenceID === tripLegReferenceID,
      );

      if (eventType === TripLegEventType.TripLegExceptionCleared) {
        if (!isOnCurrentPage) {
          // Off-page clears can't affect this page's list membership, so
          // there's nothing to do.
          return;
        }
        // A later TripLegException for the same trip leg in this same batch
        // means it became an exception again after being cleared, so this
        // clear is stale and shouldn't remove the row.
        const isSupersededByNewException = tripLegEventQueue
          .slice(index + 1)
          .some(
            (laterEvent) =>
              laterEvent.detailType === TripLegEventType.TripLegException &&
              laterEvent.detail?.tripLegReferenceID === tripLegReferenceID,
          );
        if (isSupersededByNewException) {
          return;
        }
        // The entity's isException/tripLegExceptionBC already clear
        // synchronously via the dedicated tripLegExceptionCleared reducer
        // (see updateTripLeg.ts), so drop the id from this page's list
        // directly instead of waiting on a network refetch. Without this,
        // the row visually clears its exception info first and then
        // vanishes moments later once the refetch completes, which reads as
        // a disruptive page refresh rather than a seamless update.
        dispatch(removeMonitoringExceptionsItem(tripLegReferenceID));
        removedVisibleClear = true;
        return;
      }

      if (eventType === TripLegEventType.WillCallTripLegActivated) {
        if (isOnCurrentPage) {
          // This page's row order comes from the server-sorted id list
          // captured at the last fetch, not a live client-side time sort --
          // so a newly-scheduled row stays in its old position until a
          // refetch re-sorts it. Collected below for a forced full refetch.
          visibleActivatedTripLegReferenceIDs.add(tripLegReferenceID);
          return;
        }
        // Off-page: only relevant if the new time could move this trip leg
        // onto the current page. tripLegWillCallActivated normalizes the
        // entity's search-projection fields (appointmentTimeUTC/
        // pickUpDueTimeUTC) before this effect runs, so that check can now
        // be trusted -- unconditionally hydrating/resyncing for every
        // off-page activation would disrupt the page for ones that have
        // nothing to do with what the user is viewing.
        const shouldRefresh = realtimeUtils.shouldRefreshTripLegData({
          tripLegEventType: eventType,
          tripLegEvent: event.detail,
          tripLegs:
            monitoringExceptions.items as unknown as TripLegSearchResponseItem[],
          allTripLegs: allTripLegs as unknown as TripLegSearchResponseItem[],
          filters: realtimeFilters,
        });
        if (shouldRefresh) {
          visibleActivatedTripLegReferenceIDs.add(tripLegReferenceID);
        }
        return;
      }

      // WillCallTripLegActivated is handled entirely above (both on- and
      // off-page), so it never reaches here.
      if (!EXCEPTION_REFRESH_EVENT_TYPES.includes(eventType)) {
        return;
      }

      // Assignment events can only change something we're already
      // displaying (who a visible row is assigned to). A trip leg we aren't
      // currently showing can't need that, so skip it entirely when
      // off-page instead of falling through to shouldRefreshTripLegData's
      // conservative "unknown trip leg" refetch, which would otherwise
      // refetch for any trip leg we simply haven't loaded elsewhere in the
      // app (most off-page trip legs).
      const isAssignmentEvent =
        eventType === TripLegEventType.TripLegExceptionAssigned;
      if (!isOnCurrentPage && isAssignmentEvent) {
        return;
      }

      const shouldRefresh = realtimeUtils.shouldRefreshTripLegData({
        tripLegEventType: eventType,
        tripLegEvent: event.detail,
        tripLegs:
          monitoringExceptions.items as unknown as TripLegSearchResponseItem[],
        allTripLegs: allTripLegs as unknown as TripLegSearchResponseItem[],
        filters: realtimeFilters,
      });
      if (shouldRefresh) {
        needsGeneralRefetch = true;
      }
    });

    if (visibleActivatedTripLegReferenceIDs.size > 0) {
      // tripLegWillCallActivated merges whatever fields the event payload
      // carries, but that payload's field names may not match the search
      // projection this page renders and sorts by (e.g. appointmentDateTime
      // vs appointmentDate) -- and since these entities are already loaded,
      // the syncIdsOnly refetch below won't rehydrate them either (it only
      // backfills ids that are missing). Force a full refetch of just the
      // activated entities so their search-projection fields (pickup time,
      // appointment date, etc.) are correct regardless of the event
      // payload's shape.
      dispatch(
        fetchTripsByReferenceIdsAsync({
          tripLegReferenceID: Array.from(visibleActivatedTripLegReferenceIDs),
          signal: unmountAbortControllerRef.current?.signal,
        }),
      );
    }

    const hasVisibleActivation = visibleActivatedTripLegReferenceIDs.size > 0;
    if (removedVisibleClear || hasVisibleActivation) {
      // Call the fetch directly instead of going through the debounced
      // requestRefetch path: if a search from an earlier pagination/
      // sort/filter change is still in flight, its response reflects data
      // from before this clear/activation, and would overwrite the local
      // removal (or resort need) the moment it resolves -- restoring a row
      // we just removed, along with its stale count. fetchTripsWithExceptions
      // aborts any in-flight request immediately before starting the new
      // one; waiting out the 200ms debounce would not close that race. This
      // also re-sorts the page now that the activated entities' times are
      // being corrected above, so the separate general-refetch path below
      // is skipped to avoid scheduling a redundant second fetch.
      fetchTripsWithExceptions();
    } else if (needsGeneralRefetch) {
      requestRefetch();
    }

    dispatch(removeTripLegEventFromQueue(tripLegEventQueue.length));
  }, [allTripLegs, dispatch, tripLegEventQueue, fetchTripsWithExceptions]);

  useEffect(() => {
    dispatch(
      lookupSlice.fetchLookupItemsAsync('transportationProviderPhoneUse'),
    );
  }, []);

  const isDisplayingRequestedPage =
    monitoringExceptions.filters?.page === page &&
    monitoringExceptions.filters?.pageSize === pageSize &&
    monitoringExceptions.filters?.sort === sortModel.column &&
    monitoringExceptions.filters?.sortOrder === sortModel.direction &&
    [...(monitoringExceptions.filters?.tripLegExceptionBC ?? [])]
      .sort()
      .join(',') === [...currentFilter].sort().join(',');

  return (
    <StyledBox className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {t('monitoring.exceptions')}
        </Typography>
        <TextField
          select
          value={currentFilter}
          className={classes.filter}
          required={false}
          onChange={handleTripLegFilterChange}
          label={t('tripLegException.exception')}
          variant="outlined">
          {filterOptions.map((opt) => {
            return (
              <MenuItem value={opt.value.join(',')} key={opt.value.join(',')}>
                {opt.label}
              </MenuItem>
            );
          })}
        </TextField>
      </div>
      <MonitoringExceptionsTable
        page={page}
        pageSize={pageSize}
        rowCount={monitoringExceptions.itemCount}
        handlePaginationChange={handlePaginationChange}
        handleSortModelChange={handleSortModelChange}
        tripLegs={sortedTripLegs}
        loading={
          loadingMonitoringExceptions &&
          (sortedTripLegs.length === 0 || !isDisplayingRequestedPage)
        }
      />
    </StyledBox>
  );
}
